import React, { useState, useEffect } from 'react';
import { Download } from 'lucide-react';
import Button from '../../components/ui/Button';
import Sidebar from '../../components/ui/Sidebar';
import Header from '../../components/ui/Header';
import FilterControls from './components/FilterControls';
import MetricsCard from './components/MetricsCard';
import RevenueChart from './components/RevenueChart';
import WinRateChart from './components/WinRateChart';
import ConversionFunnelChart from './components/ConversionFunnelChart';
import SampleDataToggle from '../../components/ui/SampleDataToggle';
import { sampleDataService } from '../../services/sampleDataService';
import { useAuth } from '../../contexts/AuthContext';
import { useRealtimeMultiEntity } from '../../hooks/useRealtimeMultiEntity';

import ComponentIdBadge from '../../components/ui/ComponentIdBadge';

export default function Reports() {
  const { profile } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showSampleData, setShowSampleData] = useState(false);
  const [sampleDeals, setSampleDeals] = useState([]);
  const [filters, setFilters] = useState({
    dateRange: 'last-30-days',
    selectedOwner: 'all',
    selectedStage: 'all'
  });

  // Read initial state from localStorage
  const getInitialSampleDataState = () => {
    try {
      const stored = localStorage?.getItem(`crm_sample_data_enabled_${profile?.tenant_id}`);
      return stored === 'true';
    } catch {
      return false;
    }
  };

  // Fetch real-time deals data
  const { data: realtimeData } = useRealtimeMultiEntity(
    [
      { table: 'deals', key: 'deals' },
      { table: 'contacts', key: 'contacts' }
    ],
    profile?.tenant_id
  );

  const realDeals = realtimeData?.deals || [];
  const realContacts = realtimeData?.contacts || [];

  // Merge real and sample data
  const allDeals = showSampleData ? [...realDeals, ...sampleDeals] : realDeals;

  // Calculate metrics from real data
  const calculateMetrics = () => {
    const now = new Date();
    const currentYear = now?.getFullYear();
    const currentMonth = now?.getMonth();
    
    // Filter deals based on date range
    const getDateRangeFilter = () => {
      const today = new Date();
      switch (filters?.dateRange) {
        case 'last-7-days':
          return new Date(today.setDate(today.getDate() - 7));
        case 'last-30-days':
          return new Date(today.setDate(today.getDate() - 30));
        case 'last-90-days':
          return new Date(today.setDate(today.getDate() - 90));
        case 'this-year':
          return new Date(currentYear, 0, 1);
        default:
          return new Date(today.setDate(today.getDate() - 30));
      }
    };

    const dateFilter = getDateRangeFilter();
    const filteredDeals = allDeals?.filter(deal => {
      const dealDate = new Date(deal.created_at);
      return dealDate >= dateFilter;
    });

    // Total Revenue (won deals)
    const wonDeals = filteredDeals?.filter(d => d?.stage === 'won');
    const totalRevenue = wonDeals?.reduce((sum, deal) => sum + (parseFloat(deal?.value) || 0), 0);

    // Previous period revenue for comparison
    const getPreviousPeriodRevenue = () => {
      const prevStart = new Date(dateFilter);
      const prevEnd = new Date(dateFilter);
      const daysDiff = Math.floor((now - dateFilter) / (1000 * 60 * 60 * 24));
      prevStart?.setDate(prevStart?.getDate() - daysDiff);
      
      const prevDeals = allDeals?.filter(deal => {
        const dealDate = new Date(deal.created_at);
        return dealDate >= prevStart && dealDate < dateFilter && deal?.stage === 'won';
      });
      return prevDeals?.reduce((sum, deal) => sum + (parseFloat(deal?.value) || 0), 0);
    };

    const prevRevenue = getPreviousPeriodRevenue();
    const revenueChange = prevRevenue > 0 ? (((totalRevenue - prevRevenue) / prevRevenue) * 100)?.toFixed(1) : '0.0';
    const revenueChangeType = totalRevenue >= prevRevenue ? 'positive' : 'negative';

    // Conversion Rate (contacts to won deals)
    const totalContacts = realContacts?.length + (showSampleData ? sampleDeals?.length : 0);
    const conversionRate = totalContacts > 0 ? ((wonDeals?.length / totalContacts) * 100)?.toFixed(1) : '0.0';

    // Previous period conversion for comparison
    const getPreviousConversion = () => {
      const prevStart = new Date(dateFilter);
      const daysDiff = Math.floor((now - dateFilter) / (1000 * 60 * 60 * 24));
      prevStart?.setDate(prevStart?.getDate() - daysDiff);
      
      const prevWonDeals = allDeals?.filter(deal => {
        const dealDate = new Date(deal.created_at);
        return dealDate >= prevStart && dealDate < dateFilter && deal?.stage === 'won';
      });
      
      const prevContacts = realContacts?.filter(contact => {
        const contactDate = new Date(contact.created_at);
        return contactDate >= prevStart && contactDate < dateFilter;
      });
      
      return prevContacts?.length > 0 ? ((prevWonDeals?.length / prevContacts?.length) * 100)?.toFixed(1) : 0;
    };

    const prevConversion = getPreviousConversion();
    const conversionChange = prevConversion > 0 ? (conversionRate - prevConversion)?.toFixed(1) : '0.0';
    const conversionChangeType = parseFloat(conversionChange) >= 0 ? 'positive' : 'negative';

    // Average Deal Size
    const avgDealSize = wonDeals?.length > 0 ? totalRevenue / wonDeals?.length : 0;

    // Previous period average deal size
    const getPreviousAvgDealSize = () => {
      const prevStart = new Date(dateFilter);
      const daysDiff = Math.floor((now - dateFilter) / (1000 * 60 * 60 * 24));
      prevStart?.setDate(prevStart?.getDate() - daysDiff);
      
      const prevWonDeals = allDeals?.filter(deal => {
        const dealDate = new Date(deal.created_at);
        return dealDate >= prevStart && dealDate < dateFilter && deal?.stage === 'won';
      });
      
      if (prevWonDeals?.length === 0) return 0;
      const prevTotal = prevWonDeals?.reduce((sum, deal) => sum + (parseFloat(deal?.value) || 0), 0);
      return prevTotal / prevWonDeals?.length;
    };

    const prevAvgDealSize = getPreviousAvgDealSize();
    const avgDealChange = prevAvgDealSize > 0 ? (((avgDealSize - prevAvgDealSize) / prevAvgDealSize) * 100)?.toFixed(1) : '0.0';
    const avgDealChangeType = avgDealSize >= prevAvgDealSize ? 'positive' : 'negative';

    // Sales Velocity (average days to close)
    const closedDeals = filteredDeals?.filter(d => d?.stage === 'won' && d?.actual_close_date);
    const avgDaysToClose = closedDeals?.length > 0 
      ? Math.round(closedDeals?.reduce((sum, deal) => {
          const created = new Date(deal.created_at);
          const closed = new Date(deal.actual_close_date);
          const days = Math.floor((closed - created) / (1000 * 60 * 60 * 24));
          return sum + days;
        }, 0) / closedDeals?.length)
      : 0;

    // Previous period velocity
    const getPreviousVelocity = () => {
      const prevStart = new Date(dateFilter);
      const daysDiff = Math.floor((now - dateFilter) / (1000 * 60 * 60 * 24));
      prevStart?.setDate(prevStart?.getDate() - daysDiff);
      
      const prevClosedDeals = allDeals?.filter(deal => {
        const dealDate = new Date(deal.created_at);
        return dealDate >= prevStart && dealDate < dateFilter && deal?.stage === 'won' && deal?.actual_close_date;
      });
      
      if (prevClosedDeals?.length === 0) return 0;
      return Math.round(prevClosedDeals?.reduce((sum, deal) => {
        const created = new Date(deal.created_at);
        const closed = new Date(deal.actual_close_date);
        const days = Math.floor((closed - created) / (1000 * 60 * 60 * 24));
        return sum + days;
      }, 0) / prevClosedDeals?.length);
    };

    const prevVelocity = getPreviousVelocity();
    const velocityChange = prevVelocity > 0 ? Math.abs(avgDaysToClose - prevVelocity) : 0;
    const velocityChangeType = avgDaysToClose <= prevVelocity ? 'positive' : 'negative';

    return {
      totalRevenue,
      revenueChange: `${revenueChange >= 0 ? '+' : ''}${revenueChange}%`,
      revenueChangeType,
      conversionRate: `${conversionRate}%`,
      conversionChange: `${conversionChange >= 0 ? '+' : ''}${conversionChange}%`,
      conversionChangeType,
      avgDealSize,
      avgDealChange: `${avgDealChange >= 0 ? '+' : ''}${avgDealChange}%`,
      avgDealChangeType,
      avgDaysToClose,
      velocityChange: `${velocityChange} days`,
      velocityChangeType
    };
  };

  const metrics = calculateMetrics();

  const handleSidebarToggle = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleSidebarClose = () => {
    setIsSidebarOpen(false);
  };

  const handleFiltersChange = (newFilters) => {
    setIsLoading(true);
    setFilters(newFilters);
    
    // Simulate data loading
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  };

  // Close sidebar on route change or outside click
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load sample data if toggle is already active on mount
  useEffect(() => {
    if (showSampleData && profile?.tenant_id) {
      loadSampleData();
    }
  }, [profile?.tenant_id]);

  const loadSampleData = async () => {
    try {
      const data = await sampleDataService?.getSampleDataByEntity('deals', profile?.tenant_id);
      setSampleDeals(data || []);
    } catch (err) {
      console.error('Error loading sample data:', err);
    }
  };

  const handleSampleDataToggle = async (isActive) => {
    try {
      setShowSampleData(isActive);
      if (isActive) {
        await loadSampleData();
      } else {
        setSampleDeals([]);
      }
    } catch (err) {
      console.error('Error toggling sample data:', err);
    }
  };

  const handleExport = () => {
    // Export logic here
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <ComponentIdBadge 
        id="pg-reports-main" 
        name="Reports Page"
        description="Analytics and reporting dashboard with charts and metrics"
        position="top-right"
      />
      <Sidebar isOpen={isSidebarOpen} onClose={handleSidebarClose} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header onToggleSidebar={handleSidebarToggle} />
        
        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-7xl mx-auto p-6">
            <div className="flex justify-between items-center mb-6 relative">
              <div>
                <ComponentIdBadge 
                  id="hdr-reports-title" 
                  name="Reports Page Header"
                  description="Page title and report description"
                  position="inline"
                  showLabel={true}
                />
                <h1 className="text-3xl font-bold text-gray-900">Reports & Analytics</h1>
                <p className="text-gray-600">Comprehensive business insights and performance metrics</p>
              </div>
              <div className="flex items-center gap-3">
                <SampleDataToggle
                  entityType="reports data"
                  onToggle={handleSampleDataToggle}
                  isActive={showSampleData}
                />
                <div className="relative">
                  <ComponentIdBadge 
                    id="btn-report-export" 
                    name="Export Report Button"
                    description="Downloads report data in various formats"
                  />
                  <Button onClick={handleExport} className="flex items-center gap-2">
                    <Download className="w-4 h-4" />
                    Export Report
                  </Button>
                </div>
              </div>
            </div>

            <div className="relative bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
              <ComponentIdBadge 
                id="cmp-reports-filters" 
                name="Report Filters Component"
                description="Date range and dimension filters"
              />
              <FilterControls
                dateRange={filters?.dateRange}
                setDateRange={(range) => setFilters({...filters, dateRange: range})}
                metric={filters?.selectedStage}
                setMetric={(stage) => setFilters({...filters, selectedStage: stage})}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="relative">
                <ComponentIdBadge 
                  id="crd-metric-revenue" 
                  name="Revenue Metrics Card"
                  description="Total revenue KPI with trend"
                />
                <MetricsCard 
                  title="Total Revenue" 
                  value={isLoading ? "Loading..." : `$${metrics?.totalRevenue?.toLocaleString()}`} 
                  change={metrics?.revenueChange} 
                  changeType={metrics?.revenueChangeType}
                  icon="DollarSign"
                  iconColor="bg-primary"
                  description="Total revenue generated"
                />
              </div>
              <div className="relative">
                <ComponentIdBadge 
                  id="crd-metric-conversion" 
                  name="Conversion Metrics Card"
                  description="Lead to deal conversion rate"
                />
                <MetricsCard 
                  title="Conversion Rate" 
                  value={isLoading ? "Loading..." : metrics?.conversionRate} 
                  change={metrics?.conversionChange} 
                  changeType={metrics?.conversionChangeType}
                  icon="TrendingUp"
                  iconColor="bg-success"
                  description="Lead to deal conversion"
                />
              </div>
              <div className="relative">
                <ComponentIdBadge 
                  id="crd-metric-dealsize" 
                  name="Deal Size Metrics Card"
                  description="Average deal value and growth"
                />
                <MetricsCard 
                  title="Average Deal Size" 
                  value={isLoading ? "Loading..." : `$${metrics?.avgDealSize?.toLocaleString(undefined, {maximumFractionDigits: 0})}`} 
                  change={metrics?.avgDealChange} 
                  changeType={metrics?.avgDealChangeType}
                  icon="DollarSign"
                  iconColor="bg-primary"
                  description="Mean deal value"
                />
              </div>
              <div className="relative">
                <ComponentIdBadge 
                  id="crd-metric-velocity" 
                  name="Sales Velocity Metrics Card"
                  description="Average sales cycle and performance"
                />
                <MetricsCard 
                  title="Sales Velocity" 
                  value={isLoading ? "Loading..." : `${metrics?.avgDaysToClose} days`} 
                  change={metrics?.velocityChange} 
                  changeType={metrics?.velocityChangeType}
                  icon="Clock"
                  iconColor="bg-accent"
                  description="Average sales cycle"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <div className="relative bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <ComponentIdBadge 
                  id="crd-chart-revenue" 
                  name="Revenue Trend Chart"
                  description="Line chart showing revenue over time"
                />
                <RevenueChart deals={allDeals} />
              </div>

              <div className="relative bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <ComponentIdBadge 
                  id="crd-chart-winrate" 
                  name="Win Rate Chart"
                  description="Chart displaying deal win/loss ratio"
                />
                <WinRateChart deals={allDeals} />
              </div>
            </div>

            <div className="relative bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <ComponentIdBadge 
                id="crd-chart-funnel" 
                name="Conversion Funnel Chart"
                description="Funnel visualization of sales pipeline conversion"
              />
              <ConversionFunnelChart deals={allDeals} />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}